import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  arrayMove,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Plus, Pencil, Trash2, RotateCcw, Save, X } from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle,
} from './ui/alert-dialog';
import { invalidateFieldFormOptions } from '../hooks/useFieldFormOptions';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Group keys by form so the UI is two clear blocks.
const GROUPS = [
  {
    title: 'Locate Form',
    keys: [
      'locate.locate_type',
      'locate.utility_type',
      'locate.utility_material',
      'locate.confidence_level',
      'locate.marked_color',
      'locate.markings_placed',
      'locate.surface_type',
      'locate.site_conditions',
    ],
  },
  {
    title: 'Soft Dig Form',
    keys: [
      'softdig.utility_type',
      'softdig.utility_material',
      'softdig.utility_color_code',
      'softdig.portion_exposed',
      'softdig.observed_condition',
      'softdig.surface_covering',
      'softdig.soil_condition',
    ],
  },
];

function SortableRow({ option, onChangeValue, onChangeColor, onDelete, isColorField }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: option._id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-2 px-3 py-2 bg-white border border-slate-200 rounded mb-1.5"
      data-testid={`option-row-${option.value.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing text-slate-400 hover:text-slate-600 touch-none"
        aria-label="Drag to reorder"
      >
        <GripVertical className="w-4 h-4" />
      </button>
      {isColorField && (
        <input
          type="color"
          value={option.color || '#cbd5e1'}
          onChange={(e) => onChangeColor(e.target.value)}
          className="w-8 h-8 border border-slate-300 rounded cursor-pointer p-0.5"
          aria-label="Pick color"
        />
      )}
      <Input
        value={option.value}
        onChange={(e) => onChangeValue(e.target.value)}
        className="flex-1 h-9"
        data-testid="option-value-input"
      />
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={onDelete}
        className="text-red-500 hover:text-red-600 hover:bg-red-50 h-9 w-9 p-0"
        title="Delete option"
        data-testid="option-delete"
      >
        <Trash2 className="w-4 h-4" />
      </Button>
    </div>
  );
}

function FieldEditor({ fieldKey, doc, onSaved }) {
  // Local working copy. Each option gets an internal _id so dnd-kit can
  // track it across renames (Sortable needs a stable key independent of
  // the value the user is currently editing).
  const [rows, setRows] = useState(() =>
    (doc?.options || []).map((o, i) => ({ ...o, _id: `${fieldKey}-${i}-${o.value}` }))
  );
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [showReset, setShowReset] = useState(false);
  const isColorField = doc?.kind === 'color';

  useEffect(() => {
    setRows((doc?.options || []).map((o, i) => ({ ...o, _id: `${fieldKey}-${i}-${o.value}` })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc?.updated_at, fieldKey]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setRows((items) => {
      const oldIndex = items.findIndex((i) => i._id === active.id);
      const newIndex = items.findIndex((i) => i._id === over.id);
      return arrayMove(items, oldIndex, newIndex);
    });
  };

  const addOption = () => {
    setRows((prev) => [
      ...prev,
      {
        _id: `${fieldKey}-new-${Date.now()}`,
        value: 'New option',
        color: isColorField ? '#cbd5e1' : undefined,
      },
    ]);
  };

  const updateValue = (idx, value) => {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, value } : r)));
  };
  const updateColor = (idx, color) => {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, color } : r)));
  };
  const deleteRow = (idx) => {
    setRows((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSave = async () => {
    // Strip _id and empty rows before POST.
    const cleaned = rows
      .map((r) => ({
        value: (r.value || '').trim(),
        ...(isColorField && r.color ? { color: r.color } : {}),
      }))
      .filter((r) => r.value);
    if (cleaned.length === 0) {
      toast.error('At least one option is required.');
      return;
    }
    // Catch duplicates client-side so user gets immediate feedback.
    const seen = new Set();
    for (const r of cleaned) {
      const k = r.value.toLowerCase();
      if (seen.has(k)) {
        toast.error(`Duplicate option: "${r.value}"`);
        return;
      }
      seen.add(k);
    }
    setSaving(true);
    try {
      await axios.put(`${API}/field-form-options/${encodeURIComponent(fieldKey)}`, { options: cleaned });
      invalidateFieldFormOptions();
      toast.success(`Saved — ${doc.label}`);
      onSaved && onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    setResetting(true);
    try {
      await axios.post(`${API}/field-form-options/${encodeURIComponent(fieldKey)}/reset`);
      invalidateFieldFormOptions();
      toast.success(`Reset — ${doc.label}`);
      onSaved && onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to reset');
    } finally {
      setResetting(false);
      setShowReset(false);
    }
  };

  const dirty = JSON.stringify(
    rows.map(({ _id, ...rest }) => rest) // eslint-disable-line no-unused-vars
  ) !== JSON.stringify(doc?.options || []);

  return (
    <Card className="border-slate-200" data-testid={`field-editor-${fieldKey}`}>
      <CardHeader className="pb-2 flex flex-row items-center justify-between gap-3">
        <div className="min-w-0">
          <CardTitle className="text-base font-['Barlow_Condensed']">{doc.label}</CardTitle>
          <p className="text-xs text-slate-500 font-mono">{fieldKey}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button type="button" variant="ghost" size="sm" onClick={() => setShowReset(true)}>
            <RotateCcw className="w-3.5 h-3.5 mr-1" />
            Reset
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={rows.map((r) => r._id)} strategy={verticalListSortingStrategy}>
            {rows.map((r, idx) => (
              <SortableRow
                key={r._id}
                option={r}
                isColorField={isColorField}
                onChangeValue={(v) => updateValue(idx, v)}
                onChangeColor={(c) => updateColor(idx, c)}
                onDelete={() => deleteRow(idx)}
              />
            ))}
          </SortableContext>
        </DndContext>
        <div className="flex flex-wrap items-center gap-2 mt-3">
          <Button type="button" variant="outline" size="sm" onClick={addOption} data-testid={`add-option-${fieldKey}`}>
            <Plus className="w-3.5 h-3.5 mr-1" />
            Add option
          </Button>
          <div className="flex-1" />
          <Button
            type="button"
            disabled={!dirty || saving}
            onClick={handleSave}
            className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold"
            size="sm"
            data-testid={`save-options-${fieldKey}`}
          >
            <Save className="w-3.5 h-3.5 mr-1" />
            {saving ? 'Saving…' : dirty ? 'Save changes' : 'Saved'}
          </Button>
        </div>
        {doc.updated_at && (
          <p className="text-[11px] text-slate-400 mt-2">
            Last updated {new Date(doc.updated_at).toLocaleString()}
          </p>
        )}
      </CardContent>

      <AlertDialog open={showReset} onOpenChange={setShowReset}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reset to defaults?</AlertDialogTitle>
            <AlertDialogDescription>
              Replaces this field's option list with the original defaults.
              Existing reports that reference removed values keep them as
              "removed" badges so old data is never lost.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={resetting}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleReset} disabled={resetting}>
              {resetting ? 'Resetting…' : 'Reset'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}

export default function FieldFormOptionsPanel() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({});
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/field-form-options`);
      setData(res.data || {});
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load options');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading) {
    return <div className="text-slate-500 py-8 text-center">Loading field form options…</div>;
  }
  if (error) {
    return (
      <div className="text-red-600 py-8 text-center">
        {error}
        <Button variant="outline" className="ml-3" onClick={load}>Retry</Button>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="field-form-options-panel">
      <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm text-amber-900">
        <p className="font-semibold mb-0.5">How this works</p>
        <p className="text-xs">
          Drag to reorder, edit a value to rename, click <Plus className="w-3 h-3 inline" /> to add a new option,
          or <Trash2 className="w-3 h-3 inline text-red-500" /> to remove one. Changes apply to <strong>new</strong> reports immediately.
          Existing reports keep whatever was selected on them — removed options stay visible on those reports with a small
          "removed" badge so nothing gets lost.
        </p>
      </div>

      {GROUPS.map((group) => (
        <div key={group.title}>
          <h2 className="text-xl font-bold text-slate-900 font-['Barlow_Condensed'] mb-3 tracking-tight">
            {group.title}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {group.keys.map((k) => (
              data[k] ? <FieldEditor key={k} fieldKey={k} doc={data[k]} onSaved={load} /> : null
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
